// Horizontal scene strip — free-flowing marquee that always drifts to the
// right. Mirrors the landing page <Marquee> approach (duplicate the track and
// translate -50% so it loops seamlessly).
//
// Touch behavior: we set `touch-action: pan-y` on the wrapper so the browser
// always lets the user scroll the page vertically through the strip. We only
// pause the marquee on a real horizontal drag (pointer move > 6px on X), not
// on every touch — a tap selects without ever pausing. This fixes the "page
// won't scroll when my finger lands on the scene strip" bug.
import { useRef } from "react";
import { cn } from "@/lib/utils";
import { ImageWithSkeleton } from "@/components/ui/image-with-skeleton";

export interface SceneStripItem {
  id: string;
  label: string;
  image: string;
}

interface SceneStripProps {
  items: SceneStripItem[];
  onPick: (id: string) => void;
  selectedId?: string | null;
  className?: string;
  /** Full-loop duration in seconds. Lower = faster. Defaults to 45s. */
  durationSec?: number;
}

export const SceneStrip = ({
  items,
  onPick,
  selectedId,
  className,
  durationSec = 45,
}: SceneStripProps) => {
  // We mutate the inner track via a ref (never via state) so pausing/resuming
  // doesn't reset the keyframe to translateX(0) — that would cause a "blink/jump".
  const trackRef = useRef<HTMLDivElement>(null);
  const resumeTimer = useRef<number | null>(null);
  const pointerStart = useRef<{ x: number; y: number; id: number } | null>(null);
  const horizontalDrag = useRef(false);

  const pause = () => {
    if (resumeTimer.current) {
      window.clearTimeout(resumeTimer.current);
      resumeTimer.current = null;
    }
    if (trackRef.current) trackRef.current.style.animationPlayState = "paused";
  };
  const resume = (delayMs = 0) => {
    if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => {
      if (trackRef.current) trackRef.current.style.animationPlayState = "running";
    }, delayMs);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
    horizontalDrag.current = false;
    // Don't pause yet — we wait to see if it's a horizontal drag.
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const s = pointerStart.current;
    if (!s || s.id !== e.pointerId) return;
    const dx = Math.abs(e.clientX - s.x);
    const dy = Math.abs(e.clientY - s.y);
    // Only treat as a drag once we've moved enough; horizontal-dominant pauses,
    // vertical-dominant lets the page scroll without ever pausing.
    if (!horizontalDrag.current && dx > 6 && dx > dy) {
      horizontalDrag.current = true;
      pause();
    }
  };

  const endPointer = () => {
    pointerStart.current = null;
    if (horizontalDrag.current) {
      horizontalDrag.current = false;
      resume(800);
    }
  };

  if (items.length === 0) return null;

  const Card = ({ item, keyPrefix }: { item: SceneStripItem; keyPrefix: string }) => {
    const isActive = selectedId === item.id;
    return (
      <button
        key={`${keyPrefix}-${item.id}`}
        type="button"
        onClick={() => onPick(item.id)}
        className={cn(
          "relative h-[140px] w-[112px] shrink-0 overflow-hidden rounded-2xl ring-1 ring-white/10 transition-all active:scale-95",
          isActive && "ring-2 ring-primary shadow-[0_0_24px_-4px_hsl(var(--primary))]",
        )}
      >
        <ImageWithSkeleton src={item.image} alt={item.label} eager />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <span className="absolute inset-x-0 bottom-2 px-2 text-center text-[11px] font-semibold leading-tight text-white drop-shadow">
          {item.label}
        </span>
      </button>
    );
  };

  return (
    <div
      className={cn("group relative -mx-5 flex h-[140px] w-[calc(100%+2.5rem)] overflow-hidden", className)}
      style={{ touchAction: "pan-y", contain: "layout paint size" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onPointerLeave={endPointer}
      onMouseEnter={pause}
      onMouseLeave={() => resume(0)}
    >
      <div
        ref={trackRef}
        className="flex w-max shrink-0 animate-marquee-x"
        style={{
          animationDuration: `${durationSec}s`,
          // Start the loop well into the cycle so the user never sees the
          // "snap-back to translateX(0)" frame on mount. Negative delay must be
          // smaller than duration; -8s into a 45s loop puts us ~18% in.
          animationDelay: `-${Math.min(8, durationSec / 4)}s`,
          gap: "10px",
          paddingLeft: "10px",
          willChange: "transform",
          backfaceVisibility: "hidden",
          transform: "translate3d(0,0,0)",
        }}
      >
        {items.map((item) => (
          <Card key={`a-${item.id}`} item={item} keyPrefix="a" />
        ))}
        {items.map((item) => (
          <Card key={`b-${item.id}`} item={item} keyPrefix="b" />
        ))}
      </div>
    </div>
  );
};
